import { useState } from 'react';
import {
  ThumbsUp,
  ThumbsDown,
  MoreVertical,
  Pencil,
  Trash2,
  Award,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Flag,
  CheckCircle,
  Shield,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import StarRating from './StarRating';
import type { Review } from '@/types/review.types';
import { ReviewStatus } from '@/types/review.types';

interface ReviewCardProps {
  review: Review;
  isOwner?: boolean;
  isAdmin?: boolean;
  onEdit?: (review: Review) => void;
  onDelete?: (reviewId: string) => void;
  onHelpful?: (reviewId: string) => void;
  onReport?: (reviewId: string) => void;
  onUpdateStatus?: (reviewId: string, status: ReviewStatus) => void;
  showActions?: boolean;
}

export default function ReviewCard({
  review,
  isOwner = false,
  isAdmin = false,
  onEdit,
  onDelete,
  onHelpful,
  onReport,
  onUpdateStatus,
  showActions = true,
}: ReviewCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const reviewText = review.review_text || '';
  const shouldTruncate = reviewText.length > 250;
  const displayText = shouldTruncate && !isExpanded
    ? reviewText.slice(0, 250) + '...'
    : reviewText;

  const formattedDate = formatDistanceToNow(new Date(review.createdAt), {
    addSuffix: true,
  });

  const handleHelpful = () => {
    if (onHelpful) {
      onHelpful(review._id);
    }
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '?';
    const last = lastName?.charAt(0) || '?';
    return `${first}${last}`.toUpperCase();
  };

  const userName = review.user_details
    ? `${review.user_details.first_name || 'Unknown'} ${review.user_details.last_name || 'User'}`
    : 'Unknown User';

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 transition-shadow duration-200 hover:shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          {review.user_details?.profile_url ? (
            <img
              src={review.user_details.profile_url}
              alt={userName}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-sm font-medium text-primary-700">
                {review.user_details?.initials || getInitials(review.user_details?.first_name, review.user_details?.last_name)}
              </span>
            </div>
          )}

          {/* User info */}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">
                {userName}
              </span>
              {review.is_verified_completion && (
                <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                  <Award size={12} />
                  Verified
                </span>
              )}
              {review.is_featured && (
                <span className="inline-flex items-center text-xs text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded-full">
                  Featured
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <StarRating value={review.rating} readonly size="sm" />
              <span className="text-xs text-gray-400">{formattedDate}</span>
            </div>
          </div>
        </div>

        {/* Actions Menu */}
        {showActions && (
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MoreVertical size={18} className="text-gray-400" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20">
                  {isAdmin ? (
                    // Admin actions
                    <>
                      {review.status !== ReviewStatus.APPROVED && (
                        <button
                          onClick={() => {
                            setShowMenu(false);
                            onUpdateStatus?.(review._id, ReviewStatus.APPROVED);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-green-600 hover:bg-green-50"
                        >
                          <CheckCircle size={14} />
                          Approve
                        </button>
                      )}
                      {review.status !== ReviewStatus.FLAGGED && (
                        <button
                          onClick={() => {
                            setShowMenu(false);
                            onUpdateStatus?.(review._id, ReviewStatus.FLAGGED);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-orange-600 hover:bg-orange-50"
                        >
                          <Shield size={14} />
                          Flag
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          onDelete?.(review._id);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </>
                  ) : isOwner ? (
                    // Owner actions (learner editing own review)
                    <>
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          onEdit?.(review);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Pencil size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          onDelete?.(review._id);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </>
                  ) : (
                    // Learner actions (for other users' reviews)
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        onReport?.(review._id);
                      }}
                      disabled={review.has_reported}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Flag size={14} />
                      {review.has_reported ? 'Reported' : 'Report'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Review Text */}
      <p className="text-sm text-gray-600 leading-relaxed">{displayText}</p>

      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 mt-2 font-medium"
        >
          {isExpanded ? (
            <>
              Show less <ChevronUp size={14} />
            </>
          ) : (
            <>
              Read more <ChevronDown size={14} />
            </>
          )}
        </button>
      )}

      {/* Admin Response */}
      {review.admin_response && (
        <div className="mt-4 pl-4 border-l-2 border-primary-200 bg-primary-50/50 rounded-r-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare size={14} className="text-primary-600" />
            <span className="text-xs font-medium text-primary-700">Admin Response</span>
          </div>
          <p className="text-sm text-gray-600">{review.admin_response.text}</p>
        </div>
      )}

      {/* Footer - Only show helpful buttons for learners, not admins, and not for own review */}
      {showActions && !isAdmin && !isOwner && (
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100">
          <span className="text-sm text-gray-500">Helpful?</span>
          <button
            onClick={handleHelpful}
            className={`flex items-center gap-1 text-sm transition-colors ${
              review.has_marked_helpful
                ? 'text-primary-600 hover:text-primary-700'
                : 'text-gray-500 hover:text-primary-600'
            }`}
            title={review.has_marked_helpful ? "Remove helpful" : "Mark as helpful"}
          >
            <ThumbsUp
              size={16}
              className={review.has_marked_helpful ? 'fill-primary-600' : ''}
            />
            {review.helpful_count > 0 && (
              <span className="text-xs">{review.helpful_count}</span>
            )}
          </button>
          <button
            onClick={handleHelpful}
            className={`flex items-center gap-1 text-sm transition-colors ${
              review.has_marked_helpful
                ? 'text-gray-500 hover:text-red-500'
                : 'text-gray-300 cursor-not-allowed'
            }`}
            disabled={!review.has_marked_helpful}
            title={review.has_marked_helpful ? "Remove helpful" : ""}
          >
            <ThumbsDown size={16} />
          </button>
        </div>
      )}

      {/* Admin Footer - Show review status */}
      {showActions && isAdmin && (
        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-500">Status:</span>
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
            review.status === ReviewStatus.APPROVED
              ? 'bg-green-100 text-green-700'
              : review.status === ReviewStatus.FLAGGED
              ? 'bg-orange-100 text-orange-700'
              : 'bg-gray-100 text-gray-700'
          }`}>
            {review.status === ReviewStatus.APPROVED && <CheckCircle size={12} />}
            {review.status === ReviewStatus.FLAGGED && <Shield size={12} />}
            {review.status || 'Pending'}
          </span>
          {review.helpful_count > 0 && (
            <span className="text-xs text-gray-500 ml-auto">
              {review.helpful_count} found helpful
            </span>
          )}
        </div>
      )}
    </div>
  );
}
